import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TypedFormControl, TypedFormGroup } from '@principle-theorem/ng-shared';
import {
  ICreateDemoWorkspaceRequest,
  IDemoSpace,
  REGION_DEFAULT_TIMEZONE,
  type IOrganisation,
} from '@principle-theorem/principle-core/interfaces';
import {
  Region,
  TIMEZONES,
  Timezone,
  getEnumValues,
} from '@principle-theorem/shared';

export interface IOrganisationAddDialogData extends IDemoSpace {
  userName?: string;
  userEmail?: string;
}

export type OrganisationAddFormData = ICreateDemoWorkspaceRequest;

@Component({
  selector: 'pr-organisation-add-dialog',
  templateUrl: './organisation-add-dialog.component.html',
  styleUrls: ['./organisation-add-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OrganisationAddDialogComponent {
  private _dialogRef = inject(
    MatDialogRef<
      OrganisationAddDialogComponent,
      Partial<IOrganisation> | undefined
    >
  );

  data? = inject<IOrganisationAddDialogData>(MAT_DIALOG_DATA);
  regions = getEnumValues(Region);
  timezones = TIMEZONES;
  form: TypedFormGroup<OrganisationAddFormData>;

  constructor() {
    this.form = new TypedFormGroup<OrganisationAddFormData>({
      name: new TypedFormControl<string>('', [Validators.required]),
      practiceName: new TypedFormControl<string>(undefined),
      region: new TypedFormControl<Region>(undefined, [Validators.required]),
      timezone: new TypedFormControl<Timezone>(undefined, [
        Validators.required,
      ]),
      seedFileName: new TypedFormControl(undefined, [Validators.required]),
      userName: new TypedFormControl<string>(),
      userEmail: new TypedFormControl<string>(),
    });

    if (this.data) {
      this._setupFormData(this.data);
    }

    this.form.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(({ region }) => {
        const currentTimezone = this.form.controls.timezone.value;
        if (!currentTimezone) {
          this.form.controls.timezone.setValue(
            REGION_DEFAULT_TIMEZONE[region],
            {
              emitEvent: false,
            }
          );
        }
      });
  }

  submit(): void {
    this._dialogRef.close(this.form.getRawValue());
  }

  private _setupFormData(data: IOrganisationAddDialogData): void {
    if (data.fileNames) {
      this.form.controls.seedFileName.setValidators([Validators.required]);
      this.form.controls.seedFileName.updateValueAndValidity();

      if (data.fileNames.length === 1) {
        this.form.patchValue({
          seedFileName: data.fileNames[0],
        });
      }
    }

    this.form.patchValue({
      userName: data.userName,
      userEmail: data.userEmail,
    });
  }
}
